/**
 * Conteúdo dos popups das camadas vetoriais (contrato:
 * `layer.set('popup', (feature) => ReactNode | null)`).
 */
import { useEffect, type ReactNode } from 'react';
import type { FeatureLike } from 'ol/Feature';
import Geometry from 'ol/geom/Geometry';
import type VectorSource from 'ol/source/Vector';
import { MunicipioFireCount } from '../fires/MunicipioFireCount';
import { UF_NAMES } from './catalog';
import { showHighlight } from './highlight';
import { getLoadedSource } from './loader';
import { formatRoadRef } from './olStyles';
import type { RegionLayerId } from './types';

const nf = new Intl.NumberFormat('pt-BR');

function str(v: unknown): string | null {
  if (typeof v === 'string') return v.trim() || null;
  if (typeof v === 'number' && Number.isFinite(v)) return String(v);
  return null;
}

type Row = [label: string, value: ReactNode | null];

function PopupCard(props: { kind: string; title: string; rows: Row[]; feature?: FeatureLike; footer?: ReactNode }) {
  const { kind, title, rows, feature, footer } = props;
  useEffect(() => (feature ? showHighlight(feature) : undefined), [feature]);
  const visibleRows = rows.filter(([, v]) => v !== null && v !== '');
  return (
    <div className="max-w-64 min-w-40 text-sm">
      <div className="text-xs font-medium text-mata-300">{kind}</div>
      <div className="leading-snug font-semibold text-white">{title}</div>
      {visibleRows.length > 0 && (
        <dl className="mt-1.5 grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-xs">
          {visibleRows.map(([label, value]) => (
            <div key={label} className="contents">
              <dt className="text-ink-400">{label}</dt>
              <dd className="text-ink-100">{value}</dd>
            </div>
          ))}
        </dl>
      )}
      {footer}
    </div>
  );
}

function ufLabel(uf: unknown): string | null {
  const sigla = str(uf);
  if (!sigla) return null;
  const name = UF_NAMES[sigla.toUpperCase()];
  return name ? `${name} (${sigla.toUpperCase()})` : sigla;
}

// População por código IBGE, a partir das sedes (se já carregadas).
let popIndex: { source: VectorSource; rev: number; map: Map<string, number> } | null = null;

function populationOf(cdMun: string): number | null {
  const source = getLoadedSource('sedes');
  if (!source) return null;
  const rev = source.getRevision();
  if (!popIndex || popIndex.source !== source || popIndex.rev !== rev) {
    const map = new Map<string, number>();
    for (const f of source.getFeatures()) {
      const cd = str(f.get('cd_mun'));
      const pop = f.get('pop');
      if (cd && typeof pop === 'number') map.set(cd, pop);
    }
    popIndex = { source, rev, map };
  }
  return popIndex.map.get(cdMun) ?? null;
}

export function municipioPopup(feature: FeatureLike): ReactNode | null {
  const nome = str(feature.get('nome'));
  if (!nome) return null;
  const cd = str(feature.get('cd_mun'));
  const pop = cd ? populationOf(cd) : null;
  const geometry = feature.getGeometry();
  return (
    <PopupCard
      kind="Município"
      title={nome}
      feature={feature}
      rows={[
        ['Estado', ufLabel(feature.get('uf'))],
        ['População', pop !== null ? `${nf.format(pop)} hab. (IBGE)` : null],
        ['Código IBGE', cd],
      ]}
      footer={<MunicipioFireCount cdMun={cd} geometry={geometry instanceof Geometry ? geometry : null} />}
    />
  );
}

export function terraIndigenaPopup(feature: FeatureLike): ReactNode | null {
  const nome = str(feature.get('nome'));
  if (!nome) return null;
  return (
    <PopupCard
      kind="Terra indígena"
      title={nome}
      feature={feature}
      rows={[
        ['Povo', str(feature.get('etnia'))],
        ['Situação', str(feature.get('fase'))],
      ]}
    />
  );
}

export function unidadeConservacaoPopup(feature: FeatureLike): ReactNode | null {
  const nome = str(feature.get('nome'));
  if (!nome) return null;
  return (
    <PopupCard
      kind="Unidade de conservação"
      title={nome}
      feature={feature}
      rows={[
        ['Categoria', str(feature.get('categoria'))],
        ['Proteção', str(feature.get('grupo'))],
        ['Gestão', str(feature.get('esfera'))],
      ]}
    />
  );
}

const REGION_KIND: Record<RegionLayerId, string> = {
  mesorregioes: 'Mesorregião (IBGE)',
  microrregioes: 'Microrregião (IBGE)',
  regioes_intermediarias: 'Região intermediária (IBGE)',
  regioes_imediatas: 'Região imediata (IBGE)',
  regioes_integracao_pa: 'Região de Integração do Pará',
};

/** Popup de uma camada de sub-regiões. */
export function regionPopup(id: RegionLayerId): (feature: FeatureLike) => ReactNode | null {
  return (feature) => {
    const nome = str(feature.get('nome'));
    if (!nome) return null;
    const n = feature.get('municipios');
    const parcial = feature.get('parcial') === true;
    return (
      <PopupCard
        kind={REGION_KIND[id]}
        title={nome}
        feature={feature}
        rows={[
          ['Estado', ufLabel(feature.get('uf'))],
          ['Municípios', typeof n === 'number' ? `${nf.format(n)}${parcial ? ' na Amazônia Legal' : ''}` : null],
        ]}
        footer={
          parcial ? (
            <p className="mt-1.5 text-[11px] leading-snug text-ink-400">
              Parte da região fica fora da Amazônia Legal e não aparece no mapa.
            </p>
          ) : undefined
        }
      />
    );
  };
}

/** Rio sem nome devolve null (o clique cai no município abaixo). */
export function rioPopup(feature: FeatureLike): ReactNode | null {
  const nome = str(feature.get('nome'));
  if (!nome) return null;
  return <PopupCard kind="Rio" title={nome} rows={[]} />;
}

export function rodoviaPopup(feature: FeatureLike): ReactNode | null {
  const ref = formatRoadRef(feature.get('ref'));
  const nome = str(feature.get('nome'));
  if (!ref && !nome) return null;
  return (
    <PopupCard
      kind="Rodovia"
      title={ref || nome || ''}
      rows={[['Nome', ref && nome && nome !== ref ? nome : null]]}
    />
  );
}
